import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  FileText,
  Mail,
  ShieldCheck,
  Clock,
  Check,
  Phone,
  Flame,
} from "lucide-react";
import { useIntake } from "../context/IntakeContext";

const letterFeatures = [
  { icon: FileText, text: "NLL Agent drafts your letter" },
  { icon: Mail, text: "USPS Certified Mail with proof of delivery" },
  { icon: ShieldCheck, text: "Sent from No Lawyer Lawsuit (not you)" },
  { icon: Clock, text: "14-day response deadline" },
  { icon: Check, text: "Court-ready PDF for your records" },
];

const pettyFeatures = [
  { icon: Check, text: "Everything in Standard Package", plain: true },
  { icon: Phone, text: "calls them for you", bold: "Voice of Justice" },
  { icon: Mail, text: "We keep the pressure on with follow-ups", plain: true },
  { icon: Flame, text: 'letter on day 10', bold: '"Final Notice"' },
];

export const SendLetterCard = ({ delay = 0 }) => {
  const intake = useIntake();
  return (
    <motion.div
      id="plan-letter"
      data-testid="pricing-card-send-letter"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className="relative bg-white border-2 border-[#2563EB] rounded-[2rem] p-8 sm:p-10 shadow-[0_30px_60px_-25px_rgba(37,99,235,0.35)] hover:-translate-y-1.5 transition-transform duration-300"
    >
      <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#2563EB] text-white text-sm font-bold px-6 py-1.5 rounded-full whitespace-nowrap">
        Most Popular
      </span>
      <h3 className="font-display font-semibold text-3xl text-[#111827] mt-2">
        Standard Package
      </h3>
      <div className="flex items-baseline gap-3 mt-4">
        <span className="font-display font-semibold text-6xl text-[#111827]">
          $39
        </span>
        <span className="text-lg text-[#9CA3AF]">one-time</span>
      </div>
      <p className="mt-2 text-lg font-bold text-[#4B5563]">Make it official</p>

      <ul className="mt-7 space-y-4">
        {letterFeatures.map((f) => (
          <li key={f.text} className="flex items-start gap-3.5">
            <f.icon size={20} className="text-[#2563EB] mt-0.5 shrink-0" />
            <span className="text-base sm:text-lg text-[#4B5563]">
              {f.text}
            </span>
          </li>
        ))}
      </ul>

      <div className="border-t border-[#E5E7EB] mt-8 pt-6">
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#6B7280] mb-2">
          Perfect for:
        </p>
        <p className="text-base text-[#4B5563] leading-relaxed">
          Unpaid invoices, refund disputes, small debts under $500
        </p>
      </div>

      <button
        data-testid="pricing-start-letter-button"
        onClick={() => intake.open("letter")}
        className="group mt-8 w-full flex items-center justify-center gap-3 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold text-lg py-4 rounded-2xl transition-colors duration-300"
      >
        Start Your Case
        <ArrowRight
          size={20}
          strokeWidth={2.5}
          className="transition-transform duration-300 group-hover:translate-x-1"
        />
      </button>
      <p className="text-center text-sm text-[#9CA3AF] mt-4">
        Pay when ready to send
      </p>
    </motion.div>
  );
};

export const FullPettyCard = ({ delay = 0.12 }) => {
  const intake = useIntake();
  return (
    <motion.div
      id="plan-full"
      data-testid="pricing-card-go-full-petty"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className="relative bg-[#111827] border-l-4 border-[#2563EB] rounded-[2rem] p-8 sm:p-10 shadow-[0_30px_60px_-25px_rgba(17,24,39,0.6)] hover:-translate-y-1.5 transition-transform duration-300"
    >
      <div className="flex items-start justify-between gap-4 mt-2">
        <h3 className="font-display font-semibold text-3xl text-white">
          Premium Package
        </h3>
        <span className="bg-white text-[#1D4ED8] text-sm font-bold px-5 py-1.5 rounded-full whitespace-nowrap">
          Best Value
        </span>
      </div>
      <div className="flex items-baseline gap-3 mt-4">
        <span className="font-display font-semibold text-6xl text-white">
          $79
        </span>
        <span className="text-lg text-[#6B7280]">one-time</span>
      </div>
      <p className="mt-2 text-lg font-bold text-[#93C5FD]">
        Maximum pressure. Maximum results.
      </p>

      <ul className="mt-7 space-y-4">
        {pettyFeatures.map((f, i) => (
          <li key={i} className="flex items-start gap-3.5">
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#2563EB]/25 shrink-0">
              <f.icon size={16} className="text-[#93C5FD]" />
            </span>
            <span className="text-base sm:text-lg text-[#D1D5DB] leading-snug pt-1">
              {f.bold && (
                <span className="text-[#93C5FD] font-bold">{f.bold} </span>
              )}
              {f.text}
            </span>
          </li>
        ))}
      </ul>

      <div className="border-t border-[#374151] mt-8 pt-6">
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#93C5FD] mb-2">
          Perfect for:
        </p>
        <p className="text-base text-[#9CA3AF] leading-relaxed">
          Rental deposits, contractor disputes, amounts over $500, repeat
          offenders
        </p>
      </div>

      <button
        data-testid="pricing-start-full-button"
        onClick={() => intake.open("full_petty")}
        className="group mt-8 w-full flex items-center justify-center gap-3 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold text-lg py-4 rounded-2xl transition-colors duration-300"
      >
        Start Your Case
        <ArrowRight
          size={20}
          strokeWidth={2.5}
          className="transition-transform duration-300 group-hover:translate-x-1"
        />
      </button>
      <p className="text-center text-sm text-[#6B7280] mt-4">
        Pay when ready to send
      </p>
    </motion.div>
  );
};

export const PricingSection = () => (
  <section id="pricing" className="py-16 sm:py-24">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.5 }}
        className="flex items-center gap-4 mb-6"
      >
        <span className="h-[3px] w-14 bg-[#2563EB]" />
        <span className="text-xs font-bold tracking-[0.35em] uppercase text-[#6B7280]">
          Pricing
        </span>
      </motion.div>

      <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
        <motion.h2
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#111827] max-w-2xl"
        >
          Pick your level of <span className="highlight-swash">service.</span>
        </motion.h2>
        <Link
          to="/pricing"
          data-testid="see-full-pricing-link"
          className="group flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-[#111827] border-b-2 border-[#2563EB] pb-1"
        >
          Full pricing details
          <ArrowRight
            size={16}
            className="transition-transform duration-300 group-hover:translate-x-1"
          />
        </Link>
      </div>

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-8 max-w-4xl lg:max-w-none mx-auto">
        <SendLetterCard />
        <FullPettyCard />
      </div>
    </div>
  </section>
);
